/**
 * @fileoverview Redux slice para gerenciamento de sessão, carrinho e favoritos
 * @description Gerencia estado global de autenticação e sincronização de dados
 * @module sessionSlice
 * @since 1.0.0
 */

import { createSlice } from '@reduxjs/toolkit';

/**
 * Estado inicial do slice de sessão
 * @constant
 * @type {Object}
 */
const initialState = {
  user: null,
  isAuthenticated: false,
  cart: [],
  favorites: [],
  lastSync: null
};

/**
 * Slice Redux para gerenciamento de sessão.
 * Controla autenticação, carrinho e favoritos com sincronização automática.
 * 
 * @constant
 * @type {import('@reduxjs/toolkit').Slice}
 */
const sessionSlice = createSlice({
  name: 'session',
  initialState,
  reducers: {
    /**
     * Define dados do usuário autenticado
     * @param {Object} state - Estado atual
     * @param {Object} action - Action com payload do usuário
     */
    setUser: (state, action) => {
      state.user = action.payload;
      state.isAuthenticated = !!action.payload;
    },

    /**
     * Atualiza carrinho de compras (sincronização do backend)
     * @param {Object} state - Estado atual
     * @param {Object} action - Action com payload do carrinho
     */
    updateCart: (state, action) => {
      state.cart = action.payload;
      state.lastSync = new Date().toISOString();
    },

    /**
     * Atualiza favoritos (sincronização do backend)
     * @param {Object} state - Estado atual
     * @param {Object} action - Action com payload dos favoritos
     */
    updateFavorites: (state, action) => {
      state.favorites = action.payload;
      state.lastSync = new Date().toISOString();
    },

    /**
     * Adiciona item ao carrinho localmente
     * @param {Object} state - Estado atual
     * @param {Object} action - Action com payload do item
     */
    addToCart: (state, action) => {
      const existingItem = state.cart.find(
        item => item.productId === action.payload.productId
      );
      
      if (existingItem) {
        existingItem.quantity += action.payload.quantity;
      } else {
        state.cart.push(action.payload);
      }
    },

    /**
     * Remove item do carrinho localmente
     * @param {Object} state - Estado atual
     * @param {Object} action - Action com productId
     */
    removeFromCart: (state, action) => {
      state.cart = state.cart.filter(
        item => item.productId !== action.payload
      );
    },

    /**
     * Adiciona item aos favoritos localmente
     * @param {Object} state - Estado atual
     * @param {Object} action - Action com payload do item
     */
    addToFavorites: (state, action) => {
      const exists = state.favorites.some(
        item => item.productId === action.payload.productId
      );
      
      if (!exists) {
        state.favorites.push(action.payload);
      }
    },

    /**
     * Remove item dos favoritos localmente
     * @param {Object} state - Estado atual
     * @param {Object} action - Action com productId
     */
    removeFromFavorites: (state, action) => {
      state.favorites = state.favorites.filter(
        item => item.productId !== action.payload
      );
    },

    /**
     * Limpa sessão (logout)
     * @param {Object} state - Estado atual
     */
    clearSession: (state) => {
      state.user = null;
      state.isAuthenticated = false;
      state.cart = [];
      state.favorites = [];
      state.lastSync = null;
    }
  }
});

/**
 * Actions exportadas do slice
 */
export const {
  setUser,
  updateCart,
  updateFavorites,
  addToCart,
  removeFromCart,
  addToFavorites,
  removeFromFavorites,
  clearSession
} = sessionSlice.actions;

/**
 * Selectors para acessar estado
 */
export const selectUser = (state) => state.session.user;
export const selectIsAuthenticated = (state) => state.session.isAuthenticated;
export const selectCart = (state) => state.session.cart;
export const selectFavorites = (state) => state.session.favorites;
export const selectCartTotal = (state) => 
  state.session.cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);

export default sessionSlice.reducer;
